import React, { useEffect, useState } from 'react';

import { useStyles } from './SelfTapes.styles';
import { Box, Button, CircularProgress, Grid } from '@material-ui/core';

import {
  Backdrop,
  ClapperBoardIcon,
  DocumentFileIcon,
  FullBodyIcon,
  HeadshotIcon,
  MicrophoneIcon,
  OtherImagesIcon,
  useNotificationModal,
} from '@room-match/shared-ui-components';
import { IProject } from 'shared/interfaces/IProject';
import { IAttachAuditionMedia, IAuditionAttributes } from 'shared/interfaces/IAudition';

import { auditionService } from 'shared/services/auditionService';
import { useQueryClient } from 'react-query';
import { auditionScheduleService } from 'shared/services/auditionScheduleService';
import UploadingItem from './UploadingItem';
import { mediaService } from 'shared/services/mediaService';
import Multiple from './Multiple/Multiple';

export const mockData = [
  {
    icon: <HeadshotIcon className="headshotIcon" />,
    label: 'Audition Selfie',
    info: 'Image: Landscape',
  },
  {
    icon: <FullBodyIcon className="fullBodyIcon" />,
    label: 'Full Body',
    info: 'Image: Landscape or Portrait',
  },
  {
    icon: <ClapperBoardIcon />,
    label: 'Slate (ID Video)',
    info: 'Video: Landscape',
  },
  {
    icon: <ClapperBoardIcon />,
    label: 'Videos',
    info: 'Video: Landscape',
  },
  {
    icon: <DocumentFileIcon className="documentIcon" />,
    label: 'File',
    info: 'File: .PDF',
  },
  {
    icon: <MicrophoneIcon className="microphoneIcon" />,
    label: 'Audio',
    info: 'File: .wav / .mp3',
  },
  {
    icon: <OtherImagesIcon className="headshotIcon" />,
    label: 'Other Images',
    info: 'Image: Landscape',
  },
];

interface Props {
  projectData?: IProject;
  auditionSchedID?: string;
  auditionData?: IAuditionAttributes;
  handleNext: () => void;
  self_tapes_revision_provided?: boolean;
}

const { getAuditionAttachements, attachAuditionMedia, deleteAuditionMedia, updateAuditionMedia } = auditionService();
const { markAsDoneSelfTapes } = auditionScheduleService();
const { getMediaList } = mediaService();

const SelfTapesUpload: React.FC<Props> = ({
  auditionSchedID,
  auditionData,
  handleNext,
  self_tapes_revision_provided,
}) => {
  const { data: mediaData, isLoading: isMediaLoading } = getMediaList({ media_bin: true });
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [tagsReq, settagsReq] = useState<string[]>([]);
  const [mediaAttached, setmediaAttached] = useState<{ id: string; label: string }[]>([]);
  const [requiredTags, setrequiredTags] = useState<string[]>([]);
  const { data: auditionMedia, isLoading: gettingData } = getAuditionAttachements(auditionSchedID || '');
  const { mutateAsync } = attachAuditionMedia();
  const { mutateAsync: patchMediumAttachment } = updateAuditionMedia();
  const { mutateAsync: deleteAsync } = deleteAuditionMedia();
  const { mutate: markAsDoneMutate, isLoading: isUpdateLoading } = markAsDoneSelfTapes();

  const roleData = auditionData?.casting_role.data.attributes;
  const selftapesReq = roleData?.self_tapes_requirements;
  const { handleOpen, isOpen, NotificationModal, handleSetMessage, handleChangeModalType } = useNotificationModal({
    type: 'error',
  });

  const [submitting, setsubmitting] = useState(false);

  useEffect(() => {
    if (selftapesReq) {
      const tagReq = [];
      if (selftapesReq?.audition_selfie) {
        tagReq.push('Audition Selfie');
      }
      if (selftapesReq?.full_body) {
        tagReq.push('Full Body');
      }
      if (selftapesReq?.slate) {
        tagReq.push('Slate');
      }
      if (selftapesReq?.files?.length) {
        selftapesReq.files.map((m) => tagReq.push(m));
      }
      if (selftapesReq?.other_images?.length) {
        selftapesReq.other_images.map((m) => tagReq.push(m));
      }
      if (selftapesReq?.audio?.length) {
        selftapesReq.audio.map((m) => tagReq.push(m));
      }
      if (selftapesReq?.video?.length) {
        selftapesReq.video.map((m) => tagReq.push(m));
      }

      settagsReq(tagReq);
      // if (roleData?.sides.data?.length) totalMediaReq = totalMediaReq + roleData?.sides.data?.length;

      // if (selftapesReq?.audio?.length) {
      //   totalMediaReq = totalMediaReq + selftapesReq.audio?.length;
      // }
    }
  }, [selftapesReq]);

  const bindMedia = async (values: IAttachAuditionMedia) => {
    await mutateAsync(
      {
        auditionId: auditionSchedID as string,
        payload: { ...values, ...(self_tapes_revision_provided && { self_tapes_revision_provided: true }) },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['audition_attachments', auditionSchedID]);
        },
        onError: (err: any) => {
          if (err.response.data.errors.base[0].includes('Medium has already been taken')) {
            handleSetMessage(
              'Media Attachment Error',
              `You cannot use this media again as you have already attached it in this submission.`,
              'OK',
            );
            handleOpen();
          }
        },
      },
    );
  };

  const invalidFileFormat = (fileRequired: 'video' | 'audio' | 'image' | 'document' | 'invalid_image') => {
    handleSetMessage(
      'Media Attachment Error',
      fileRequired === 'invalid_image'
        ? 'Image type not supported'
        : `Invalid file type. File selected is not a valid ${fileRequired} file.`,
      'OK',
    );
    handleOpen();
  };

  const invalidImageOrientation = () => {
    handleSetMessage(
      'Warning',
      `You have uploaded the wrong image format. This should be in landscape (sideways) and not in portrait (upright) format. Please replace it.`,
      'OK',
    );
    handleChangeModalType('warning');
    handleOpen();
  };
  const deleteMedia = async (tag: string) => {
    const mediumId = mediaAttached.find((ma) => ma.label === tag.toLowerCase())?.id || '';

    await deleteAsync(
      { auditionId: auditionSchedID as string, mediumId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('audition_attachments');
        },
      },
    );
  };

  useEffect(() => {
    const rTAgs = [...tagsReq];

    if (auditionMedia) {
      const attachedMedia = auditionMedia?.data.map((m) => {
        m.attributes.tag_list.map((m) => {
          const tagIndex = rTAgs.indexOf(m);
          if (tagIndex > -1) {
            rTAgs.splice(tagIndex, 1);
          }
        });

        return { id: m.id, label: m.attributes.tag_list.toString().toLocaleLowerCase() };
      });
      setmediaAttached(attachedMedia);
      setrequiredTags(rTAgs);
      console.log('reqtag- rTags', rTAgs);
    }
  }, [auditionMedia, tagsReq]);

  const renderItem = (
    i: any,
    type: 'Image' | 'Video' | 'Audio' | 'File' | 'Other Image',
    label: string,
    tag_list: string[],
    sort: number,
  ) => {
    return (
      <UploadingItem
        invalidFileFormat={invalidFileFormat}
        invalidImageOrientation={invalidImageOrientation}
        sort={sort}
        deleteMedia={deleteMedia}
        tag_list={tag_list}
        i={i}
        label={label}
        type={type}
        bindMedia={bindMedia}
        completed={mediaAttached.map((m) => m.label).indexOf(tag_list.toString().toLocaleLowerCase()) > -1}
        media={
          auditionMedia?.data.filter(
            (f) => f.attributes.tag_list.toString().toLowerCase() === tag_list.toString().toLocaleLowerCase(),
          )[0]
        }
        hasAudio={mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'audio')?.length > 0 : !mediaData}
        hasVideo={mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'video')?.length > 0 : !mediaData}
        hasImage={mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'image')?.length > 0 : !mediaData}
      />
    );
  };

  //if self_tapes_revision_provided check if all medium attachments are marcked as self_tapes_revision_provided: true else update them
  const patchMediumAttachments = async () => {
    const mediaToPatch = auditionMedia?.data.filter((m) => !m.attributes.self_tapes_revision_provided);
    if (mediaToPatch?.length) {
      await Promise.all(
        mediaToPatch.map(async (m) => {
          await patchMediumAttachment({
            auditionId: auditionSchedID || '',
            medium_id: m.id,
            payload: { self_tapes_revision_provided: true },
          });
        }),
      );
    }
  };

  const markAsDone = async () => {
    if (self_tapes_revision_provided) await patchMediumAttachments();
    setsubmitting(true);
    const role_id = auditionData?.casting_role.data.id || '';
    const profile_id = auditionData?.profile.data.id || '';
    markAsDoneMutate(
      { auditionId: auditionSchedID || '', payload: { role_id, profile_id } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['auditionSchedule'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['audition_schedule'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['auditionRequest'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['me'], {
            refetchInactive: true,
          });
          handleNext();
          setsubmitting(false);
        },
        onError: (err: any) => setsubmitting(false),
      },
    );
  };
  if (gettingData) return <Backdrop isLoading={gettingData} />;

  return (
    <Grid className={classes.root}>
      {renderItem(mockData[0], 'Image', 'Audition Selfie', ['Audition Selfie'], 9000)}
      {selftapesReq?.full_body && renderItem(mockData[1], 'Image', 'Full Body', ['Full Body'], 10000)}
      {selftapesReq?.slate && renderItem(mockData[2], 'Video', 'Slate (ID Video)', ['Slate'], 1)}

      {/* NEW UI */}
      {selftapesReq?.other_images?.length ? (
        <Multiple
          invalidImageOrientation={invalidImageOrientation}
          invalidFileFormat={invalidFileFormat}
          sort={7000}
          media={auditionMedia?.data
            .filter((f) => f.attributes.tag_list.includes('Other Image'))
            .map((image) => {
              // Convert TIF images to JPEG before passing them to the component
              if (image.attributes.file_type === 'image' && image.attributes.file_format === 'tif') {
                return {
                  ...image,
                  attributes: {
                    ...image.attributes,
                    file_format: 'jpeg', // Change the format to JPEG
                  },
                };
              }
              return image;
            })}
          req={selftapesReq?.other_images}
          multiple={selftapesReq?.other_images_multiple}
          mock={mockData[6]}
          type={'Other Image'}
          bindMedia={bindMedia}
          deleteMedia={deleteMedia}
          completed={false}
          hasAudio={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'audio')?.length > 0 : !mediaData
          }
          hasVideo={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'video')?.length > 0 : !mediaData
          }
          hasImage={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'image')?.length > 0 : !mediaData
          }
        />
      ) : (
        <React.Fragment />
      )}

      {selftapesReq?.video?.length ? (
        <Multiple
          invalidImageOrientation={invalidImageOrientation}
          invalidFileFormat={invalidFileFormat}
          sort={1000}
          media={auditionMedia?.data.filter((f) => f.attributes.tag_list.includes('Video'))}
          req={selftapesReq?.video}
          multiple={selftapesReq?.video_multiple}
          mock={mockData[3]}
          type={'Video'}
          bindMedia={bindMedia}
          deleteMedia={deleteMedia}
          completed={false}
          hasAudio={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'audio')?.length > 0 : !mediaData
          }
          hasVideo={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'video')?.length > 0 : !mediaData
          }
          hasImage={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'image')?.length > 0 : !mediaData
          }
        />
      ) : (
        <React.Fragment />
      )}

      {selftapesReq?.audio?.length ? (
        <Multiple
          invalidImageOrientation={invalidImageOrientation}
          invalidFileFormat={invalidFileFormat}
          sort={3000}
          media={auditionMedia?.data.filter((f) => f.attributes.tag_list.includes('Audio'))}
          req={selftapesReq?.audio}
          multiple={selftapesReq?.audio_multiple}
          mock={mockData[5]}
          type={'Audio'}
          bindMedia={bindMedia}
          deleteMedia={deleteMedia}
          completed={false}
          hasAudio={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'audio')?.length > 0 : !mediaData
          }
          hasVideo={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'video')?.length > 0 : !mediaData
          }
          hasImage={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'image')?.length > 0 : !mediaData
          }
        />
      ) : (
        <React.Fragment />
      )}

      {selftapesReq?.files?.length ? (
        <Multiple
          invalidFileFormat={invalidFileFormat}
          invalidImageOrientation={invalidImageOrientation}
          sort={5000}
          media={auditionMedia?.data.filter((f) => f.attributes.tag_list.includes('File'))}
          req={selftapesReq?.files}
          multiple={selftapesReq?.files_multiple}
          mock={mockData[4]}
          type={'File'}
          bindMedia={bindMedia}
          deleteMedia={deleteMedia}
          completed={false}
          hasAudio={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'audio')?.length > 0 : !mediaData
          }
          hasVideo={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'video')?.length > 0 : !mediaData
          }
          hasImage={
            mediaData ? mediaData.data.filter((v) => v.attributes.file_type === 'image')?.length > 0 : !mediaData
          }
        />
      ) : (
        <React.Fragment />
      )}

      <Box style={{ width: '100%', display: 'flex', marginTop: 20, alignItems: 'center' }}>
        {/* {!data.data && (
          <Typography style={{ color: '#e34343', marginLeft: 'auto' }}>Please confirm Represention and Role</Typography>
        )} */}
        <Button
          variant="contained"
          color="primary"
          disabled={!!requiredTags.length}
          onClick={markAsDone}
          className={classes.doneButton}
        >
          {submitting ? (
            <>
              <CircularProgress color="inherit" style={{ width: 20, height: 20, marginRight: 5 }} /> Saving...
            </>
          ) : (
            'Submit Self-tape'
          )}
        </Button>
      </Box>
      {isOpen && <NotificationModal onClose={() => {}} />}
    </Grid>
  );
};

export default SelfTapesUpload;
